import { languageUtils } from './index';

// Define translation interface
export interface Translations {
  [key: string]: {
    [language: string]: string;
  };
}

// Default translations for the app
const defaultTranslations: Translations = {
  // Main page
  'title.main': {
    en: 'Foreign Currency to CZK Conversion',
    cs: 'Přepočet z cizí měny do CZK',
    sk: 'Prepočet z cudzej meny na CZK',
    de: 'Umrechnung von Fremdwährung in CZK',
  },
  'table.country': {
    en: 'Country',
    cs: 'Země',
    sk: 'Krajina',
    de: 'Land',
  },
  'table.currency': {
    en: 'Currency',
    cs: 'Měna',
    sk: 'Mena',
    de: 'Währung',
  },
  'table.rate': {
    en: 'Exchange Rate',
    cs: 'Kurz',
    sk: 'Kurz',
    de: 'Wechselkurs',
  },
  'footnote.xdr': {
    en: '* Special Drawing Rights',
    cs: '* Zvláštní práva čerpání - Special Drawing Rights',
    sk: '* Špeciálne práva čerpania - Special Drawing Rights',
    de: '* Sonderziehungsrechte - Special Drawing Rights',
  },

  // Older data section
  'section.olderData': {
    en: 'Older Data',
    cs: 'Starší data',
    sk: 'Staršie dáta',
    de: 'Ältere Daten',
  },
  'section.historicalJzk': {
    en: 'Historical JŽK',
    cs: 'Předchozí JŽK',
    sk: 'Predchádzajúce JŽK',
    de: 'Frühere JŽK',
  },
  'section.historicalDescription': {
    en: 'Overview of older foreign currency to CZK conversions that are no longer valid.',
    cs: 'Přehled starších přepočtů z cizí měny do CZK, které již nejsou v platnosti.',
    sk: 'Prehľad starších prepočtov z cudzej meny na CZK, ktoré už nie sú platné.',
    de: 'Übersicht über ältere Umrechnungen von Fremdwährungen in CZK, die nicht mehr gültig sind.',
  },
  'section.validityExpires': {
    en: 'On {date}, JŽK no. {number}/{year} will expire.',
    cs: 'Uplynutím dne {date} pozbývá platnosti JŽK č. {number}/{year}.',
    sk: 'Uplynutím dňa {date} stráca platnosť JŽK č. {number}/{year}.',
    de: 'Am {date} läuft JŽK Nr. {number}/{year} ab.',
  },
  'button.show': {
    en: 'Show',
    cs: 'Zobrazit',
    sk: 'Zobraziť',
    de: 'Anzeigen',
  },
  'button.back': {
    en: 'Back',
    cs: 'Zpět',
    sk: 'Späť',
    de: 'Zurück',
  },

  // Historical data page
  'title.historical': {
    en: 'Historical JŽK',
    cs: 'Předchozí JŽK',
    sk: 'Predchádzajúce JŽK',
    de: 'Frühere JŽK',
  },
  'table.number': {
    en: 'Number',
    cs: 'Číslo',
    sk: 'Číslo',
    de: 'Nummer',
  },
  'table.validFrom': {
    en: 'Valid From',
    cs: 'Platnost od',
    sk: 'Platnosť od',
    de: 'Gültig ab',
  },
  'table.file': {
    en: 'File',
    cs: 'Soubor',
    sk: 'Súbor',
    de: 'Datei',
  },
  'button.download': {
    en: 'Download file',
    cs: 'Stáhnout soubor',
    sk: 'Stiahnuť súbor',
    de: 'Datei herunterladen',
  },

  // Loading and error states
  'loading.rates': {
    en: 'Loading exchange rates...',
    cs: 'Načítání kurzů...',
    sk: 'Načítavanie kurzov...',
    de: 'Lade Wechselkurse...',
  },
  'loading.historicalData': {
    en: 'Loading historical data...',
    cs: 'Načítání historických dat...',
    sk: 'Načítavanie historických údajov...',
    de: 'Lade historische Daten...',
  },
  'error.loadingRates': {
    en: 'Failed to load exchange rates.',
    cs: 'Nepodařilo se načíst kurzy.',
    sk: 'Nepodarilo sa načítať kurzy.',
    de: 'Fehler beim Laden der Wechselkurse.',
  },
  'error.loadingHistoricalData': {
    en: 'Failed to load historical data.',
    cs: 'Nepodařilo se načíst historická data.',
    sk: 'Nepodarilo sa načítať historické údaje.',
    de: 'Fehler beim Laden der historischen Daten.',
  },

  // Countries
  'country.switzerland': {
    en: 'Switzerland',
    cs: 'Švýcarsko',
    sk: 'Švajčiarsko',
    de: 'Schweiz',
  },
  'country.denmark': {
    en: 'Denmark',
    cs: 'Dánsko',
    sk: 'Dánsko',
    de: 'Dänemark',
  },
  'country.emu': {
    en: 'EMU-EURO',
    cs: 'EMU-EURO',
    sk: 'EMU-EURO',
    de: 'EMU-EURO',
  },
  'country.hungary': {
    en: 'Hungary',
    cs: 'Maďarsko',
    sk: 'Maďarsko',
    de: 'Ungarn',
  },
  'country.poland': {
    en: 'Poland',
    cs: 'Polsko',
    sk: 'Poľsko',
    de: 'Polen',
  },
  'country.sweden': {
    en: 'Sweden',
    cs: 'Švédsko',
    sk: 'Švédsko',
    de: 'Schweden',
  },
  'country.ukraine': {
    en: 'Ukraine',
    cs: 'Ukrajina',
    sk: 'Ukrajina',
    de: 'Ukraine',
  },
  'country.xdr': {
    en: 'SDR *',
    cs: 'ZPČ-SDR *',
    sk: 'ŠPČ-SDR *',
    de: 'SZR *',
  },
  'country.uk': {
    en: 'United Kingdom',
    cs: 'Velká Británie',
    sk: 'Veľká Británia',
    de: 'Vereinigtes Königreich',
  },
  'country.usa': {
    en: 'USA',
    cs: 'USA',
    sk: 'USA',
    de: 'USA',
  },
  'country.norway': {
    en: 'Norway',
    cs: 'Norsko',
    sk: 'Nórsko',
    de: 'Norwegen',
  },
  'country.slovakia': {
    en: 'Slovakia (old)',
    cs: 'Slovensko (stará)',
    sk: 'Slovensko (stará)',
    de: 'Slowakei (alt)',
  },
  'country.bulgaria': {
    en: 'Bulgaria',
    cs: 'Bulharsko',
    sk: 'Bulharsko',
    de: 'Bulgarien',
  },
  'country.russia': {
    en: 'Russia',
    cs: 'Rusko',
    sk: 'Rusko',
    de: 'Russland',
  },
  'country.turkey': {
    en: 'Turkey',
    cs: 'Turecko',
    sk: 'Turecko',
    de: 'Türkei',
  },
  'country.romania': {
    en: 'Romania',
    cs: 'Rumunsko',
    sk: 'Rumunsko',
    de: 'Rumänien',
  },
  'country.croatia': {
    en: 'Croatia',
    cs: 'Chorvatsko',
    sk: 'Chorvátsko',
    de: 'Kroatien',
  },
  'country.serbia': {
    en: 'Serbia',
    cs: 'Srbsko',
    sk: 'Srbsko',
    de: 'Serbien',
  },
  'country.macedonia': {
    en: 'North Macedonia',
    cs: 'Makedonie',
    sk: 'Macedónsko',
    de: 'Nordmazedonien',
  },
};

// Translation utility functions
export const i18n = {
  // Main translation function with interpolation support
  t: (key: string, params?: Record<string, string> | string): string => {
    const currentLang = languageUtils.getCurrentLanguage();
    const fallback = typeof params === 'string' ? params : undefined;

    // First try to get translation from WordPress localized data
    try {
      const pluginData = window.cdcargoJzkPlugin;
      if (pluginData?.translations?.[key]?.[currentLang]) {
        let translation = pluginData.translations[key][currentLang];

        // Interpolate parameters if provided
        if (params && typeof params === 'object') {
          Object.entries(params).forEach(([paramKey, paramValue]) => {
            translation = translation.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), paramValue);
          });
        }

        return translation;
      }
    } catch {
      // Silent fail, continue to default translations
    }

    // Fallback to default translations
    const translation = defaultTranslations[key];
    if (translation && translation[currentLang]) {
      let result = translation[currentLang];

      // Interpolate parameters if provided
      if (params && typeof params === 'object') {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          result = result.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), paramValue);
        });
      }

      return result;
    }

    // Fallback to Czech if current language not found
    if (translation && translation.cs) {
      let result = translation.cs;

      // Interpolate parameters if provided
      if (params && typeof params === 'object') {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          result = result.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), paramValue);
        });
      }

      return result;
    }

    // Final fallback to provided fallback or key
    return fallback || key;
  },

  // Get current language
  getLanguage: (): string => {
    return languageUtils.getCurrentLanguage();
  },

  // Check if a translation exists
  hasTranslation: (key: string): boolean => {
    const currentLang = languageUtils.getCurrentLanguage();

    try {
      const pluginData = window.cdcargoJzkPlugin;
      if (pluginData?.translations?.[key]?.[currentLang]) {
        return true;
      }
    } catch {
      // Continue to check default translations
    }

    return !!(defaultTranslations[key] && defaultTranslations[key][currentLang]);
  },

  // Add or update translations at runtime
  addTranslations: (newTranslations: Translations): void => {
    Object.keys(newTranslations).forEach((key) => {
      if (!defaultTranslations[key]) {
        defaultTranslations[key] = {};
      }
      Object.assign(defaultTranslations[key], newTranslations[key]);
    });
  },

  // Get all available translations for debugging
  getAllTranslations: (): Translations => {
    return defaultTranslations;
  },
};

// Hook for using translations in React components
export const useTranslation = () => {
  return {
    t: i18n.t,
    language: i18n.getLanguage(),
    hasTranslation: i18n.hasTranslation,
  };
};
